import React, { useCallback, useEffect, useRef, useState } from 'react';

import useSimpleScrollLock from '../../hooks/useSimpleScrollLock';
import useClickOutside from '../../hooks/useClickOutside';
import useSaveChangesPrompt from '../../hooks/useSaveChangesPrompt';
import { ButtonV3 } from '../ButtonV3';
import { XIcon } from '../SvgIconsV3/X';
import { ArrowNarrowLeftIcon } from '../SvgIconsV3/ArrowNarrowLeft';

import {
  Overlay,
  Container,
  HeaderBar,
  CloseButton,
  BackButton,
  ScreenReaderText,
  ScrollContainer,
  UnsavedChangesPrompt,
  PromptOverlay,
  PromptTitle,
  PromptButtonContainer,
  PromptButton,
  PromptDiscard,
  DrawerTitle,
  DrawerContent,
  DrawerFooter,
  FooterButtonContainer,
} from './SideDrawerV2.styles';

export type DrawerProps = {
  /** A function to perform when the user closes the modal. Is in charge of setting isOpen to false or removing the drawer from the DOM. */
  onClose: () => void;
  /** Called when the Save button is pressed. If `false` is returned, prevents the drawer from closing. */
  onSave?: () => void | boolean | Promise<boolean | void>;
  /** Should the drawer be visible?. Defaults to `false` */
  isOpen?: boolean;
  /** Class name applied to the drawer container */
  className?: string;
  /** Locks background scrolling in place. Default true */
  lockBgScrolling?: boolean;
  /** Should the drawer close when the 'escape' key is press.  Defaults to `true` */
  closeOnEscape?: boolean;
  /** Should show close OR back icon. Defaults to `true` */
  useBackButton?: boolean;
  /** Causes "Close" actions to open a confirmation prompt */
  confirmOnClose?: boolean;
  /** Optional drawer header text */
  title?: string;
  /** What copy to show in the footer cancel button. Defaults to "Cancel" */
  cancelButtonText?: string;
  /** What copy to show in the footer save button. Defaults to "Save" */
  saveButtonText?: string;
  /** What copy to show in the unsaved changes prompt. Defaults to "You have unsaved changes." */
  confirmationPromptText?: string;
  /** Used to force disable the footer save button (e.g. when form validation fails). Button already disables during onSave promise. */
  saveButtonDisabled?: boolean;
  /** Used to override the default data-testid */
  testId?: string;
  /** Extra element to display on the left side of the footer */
  extraFooterElement?: React.ReactNode;
};

export const SideDrawerV2: React.FC<DrawerProps> = ({
  isOpen = false,
  onClose,
  onSave,
  lockBgScrolling = true,
  closeOnEscape = true,
  useBackButton = false,
  title,
  cancelButtonText = 'Cancel',
  saveButtonText = 'Save',
  confirmationPromptText = 'You have unsaved changes.',
  confirmOnClose,
  saveButtonDisabled,
  extraFooterElement,
  className,
  children,
  testId = 'zui-Drawer',
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isSavePromptOpen, setIsSavePromptOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /** This will catch a route change if it can't be caught internally, i.e. a full-page refresh route change vs shallow change of react-router; */
  useSaveChangesPrompt(!!confirmOnClose);

  /** locks background from scrolling when open */
  useSimpleScrollLock(lockBgScrolling && isOpen);

  /** Tries to close the drawer, but shows a confirmation prompt if confirmOnClose is true */
  const softCloseDrawer = useCallback(() => {
    if (confirmOnClose) {
      setIsSavePromptOpen(true);
    } else {
      onClose();
    }
  }, [onClose, confirmOnClose]);

  /**  Calls the onSave function, then closes the drawer (unless the onSave response is `false`) */
  const handleSave = useCallback(() => {
    // Do nothing if the onSave function isn't specified
    if (!onSave) return;

    setIsSaving(true);

    // Always resolve onSave so it works whether a promise is returned or not
    Promise.resolve(onSave())
      .then((shouldClose) => {
        // Unless explicitly specified, close the drawer
        if (shouldClose !== false) {
          setIsSavePromptOpen(false);
          onClose();
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  }, [onSave, onClose]);

  /** Simply hides the save prompt */
  const handlePromptContinue = useCallback(() => {
    setIsSavePromptOpen(false);
  }, []);

  /** Hides the save prompt, then closes the drawer */
  const handlePromptDiscard = useCallback(() => {
    setIsSavePromptOpen(false);
    onClose();
  }, [onClose]);

  /** When the user clicks outside the drawer, tries to close the drawer */
  useClickOutside({
    ref,
    onClickOutside: softCloseDrawer,
    isOpen,
  });

  /** When user presses "Esc", tries to close the drawer */
  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEscape) {
        event.preventDefault();
        event.stopPropagation();
        softCloseDrawer();
      }
    };
    window.addEventListener('keydown', close, true);

    return function removeEventListenerAndReturnFocus() {
      window.removeEventListener('keydown', close, true);
    };
  }, [closeOnEscape, softCloseDrawer]);

  return (
    <Overlay data-testid={testId} isOpen={isOpen}>
      <Container isOpen={isOpen} ref={ref} className={className}>
        <ScrollContainer id="drawer-scroll-container">
          <HeaderBar>
            {useBackButton ? (
              <BackButton onClick={softCloseDrawer} data-testid="drawer-back">
                <ArrowNarrowLeftIcon height={20} width={20} showTitle={false} />
                <ScreenReaderText>Back</ScreenReaderText>
              </BackButton>
            ) : (
              <CloseButton onClick={softCloseDrawer} data-testid="drawer-close">
                <XIcon height={20} width={20} showTitle={false} />
                <ScreenReaderText>Close</ScreenReaderText>
              </CloseButton>
            )}
          </HeaderBar>
          <DrawerContent>
            {!!title && <DrawerTitle>{title}</DrawerTitle>}
            {children}
          </DrawerContent>
          <DrawerFooter>
            {extraFooterElement}
            <FooterButtonContainer>
              {onSave ? (
                <>
                  <ButtonV3
                    variant="secondary"
                    onClick={softCloseDrawer}
                    disabled={isSaving}
                    data-testid="drawer-cancel"
                  >
                    {cancelButtonText}
                  </ButtonV3>
                  <ButtonV3
                    onClick={handleSave}
                    disabled={isSaving || saveButtonDisabled}
                    data-testid="drawer-save"
                  >
                    {saveButtonText}
                  </ButtonV3>
                </>
              ) : (
                <ButtonV3 onClick={softCloseDrawer} data-testid="drawer-cancel">
                  {cancelButtonText}
                </ButtonV3>
              )}
            </FooterButtonContainer>
          </DrawerFooter>
          {isSavePromptOpen && (
            <PromptOverlay>
              <UnsavedChangesPrompt data-testid="zui-Drawer__prompt">
                <PromptTitle>{confirmationPromptText}</PromptTitle>
                <PromptButtonContainer>
                  <PromptButton
                    onClick={handlePromptContinue}
                    variant={onSave ? 'secondary' : 'primary'}
                    size="small"
                    data-testid="drawer-confirmation-cancel"
                  >
                    Keep editing
                  </PromptButton>
                  {!!onSave && (
                    <PromptButton
                      onClick={handleSave}
                      size="small"
                      disabled={isSaving || saveButtonDisabled}
                      data-testid="drawer-confirmation-save"
                    >
                      Save
                    </PromptButton>
                  )}
                </PromptButtonContainer>
                <PromptDiscard
                  role="button"
                  onClick={handlePromptDiscard}
                  data-testid="drawer-confirmation-discard"
                >
                  Discard
                </PromptDiscard>
              </UnsavedChangesPrompt>
            </PromptOverlay>
          )}
        </ScrollContainer>
      </Container>
    </Overlay>
  );
};
